import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getDatabase, ref, get, onValue, remove } from 'firebase/database';
import { useSchools } from './SchoolsContext';
import { useUser } from './UserContext';
import { usePrerequisites } from './PrerequisiteContext';
import { useCoursePrerequisites } from './CoursePrerequisiteContext';
import { activityLogService } from '../services/activityLogService';
import { eligibilityRefreshService } from '../services/eligibilityRefreshService';

// Define the interfaces locally since they're not exported from the service
interface EligibilityResult {
  ifEligible: boolean | null;
  isOnRightPath: string; // Use string to match generateHTML logic: "0", "1", "2"
  alertMessage: string;
  nextYearSuggestion?: string;
  calculatedAverage?: number;
  programAverage?: number;
  missingCourses?: string[];
  gradeIssues?: string[];
  coursesUsedInAverage?: string[];
  coursesWithGradeIssues?: string[];
}

interface EligibilityData {
  calcResult: { [programId: string]: EligibilityResult };
  grade: string;
  courses: any;
  schoolsAndPrograms: any;
  name: string;
  hs: string;
  prereqs: any;
  userData: any;
}

export interface Program {
  id: string;
  program: string;
  programCode: string;
  estimatedMinimum: string;
  prerequisites: string;
  experientialLearning: string;
  province: string;
  category: string;
  description: string;
  degree: string;
  language: string;
  domesticTuition: string;
  internationalTuition: string;
  additionalRequirements: string;
  url: string;
  schoolName: string;
  ProgramLastDeleted?: number;
  // Add eligibility fields
  eligibility?: {
    isEligible: boolean | null;
    alertMessage?: string;
    nextYearSuggestion?: string;
    calculatedAverage?: number;
    programAverage?: number;
    missingCourses?: string[];
    gradeIssues?: string[];
    coursesUsedInAverage?: string[];
    coursesWithGradeIssues?: string[];
  };
}

interface ProgramsContextType {
  programs: Program[];
  loading: boolean;
  error: string | null;
  refreshPrograms: () => void;
  // Add eligibility methods
  eligibilityLoading: boolean;
  eligibilityError: string | null;
  eligibilityData: EligibilityData | null;
  checkEligibilityForSchool: (schoolID: string) => Promise<void>;
  clearEligibility: () => void;
  // Add new efficient eligibility methods
  calculateAllEligibility: () => Promise<void>;
  calculateEligibilityForSchools: (schoolNames: string[]) => Promise<void>;
  calculateEligibilityForAllPrograms: () => Promise<void>;
  isEligibilityCalculated: () => boolean;
  isEligibilityCalculatedForSchools: (schoolNames: string[]) => boolean;
  getProgramEligibility: (programId: string) => EligibilityResult | null;
  refreshEligibilityIfNeeded: () => Promise<void>;
  eligibilityCalculationInProgress: boolean;
  exportEligibilityCacheToFirebase: () => Promise<void>;
  // Firebase-only architecture state
  cacheLoading: boolean;
  cacheError: string | null;
  userDataStatus: 'loading' | 'complete' | 'incomplete' | 'error';
  showOnboardingModal: boolean;
  setShowOnboardingModal: (show: boolean) => void;
  triggerEligibilityCalculation: () => Promise<void>;
  globalEligibilityCache: { [programId: string]: EligibilityResult };
  handleOnboardingComplete: () => Promise<void>;
  clearErrors: () => void;
  clearEligibilityError: () => void;
  forceClearAllErrors: () => void;
  forceClearCacheAndErrors: () => Promise<void>;
}

const ProgramsContext = createContext<ProgramsContextType | undefined>(undefined);

export const usePrograms = () => {
  const context = useContext(ProgramsContext);
  if (context === undefined) {
    throw new Error('usePrograms must be used within a ProgramsProvider');
  }
  return context;
};

interface ProgramsProviderProps {
  children: ReactNode;
}

export const ProgramsProvider: React.FC<ProgramsProviderProps> = ({ children }) => {
  console.log('🔄 ProgramsProvider: Component mounted/rendered');
  
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug: Log when ProgramsProvider mounts/unmounts
  useEffect(() => {
    console.log('🔄 ProgramsProvider: Mounted');
    return () => {
      console.log('🔄 ProgramsProvider: Unmounted');
    };
  }, []);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  
  // Simplified debug wrapper for setEligibilityError - only log when setting error
  const setEligibilityErrorWithLog = (error: string | null) => {
    if (error) {
      console.log('🚨 ERROR SET: eligibilityError =', error);
      console.log('🚨 ERROR SOURCE:', new Error().stack?.split('\n')[2]?.trim());
    }
    setEligibilityError(error);
  };
  const [eligibilityData, setEligibilityData] = useState<EligibilityData | null>(null);
  // Add global eligibility cache with more intelligent caching
  const [globalEligibilityCache, setGlobalEligibilityCache] = useState<{ [programId: string]: EligibilityResult }>({});
  const [eligibilityCacheKey, setEligibilityCacheKey] = useState<string>('');
  const [lastPrerequisitesHash, setLastPrerequisitesHash] = useState<string>('');
  const [eligibilityCalculationInProgress, setEligibilityCalculationInProgress] = useState(false);
  const [lastUserCoursesHash, setLastUserCoursesHash] = useState<string>('');
  const [cacheLoaded, setCacheLoaded] = useState<boolean>(false);
  const [eligibilityCalculationLock, setEligibilityCalculationLock] = useState<boolean>(false);
  // Firebase-only architecture state
  const [cacheLoading, setCacheLoading] = useState(true);
  const [cacheError, setCacheError] = useState<string | null>(null);
  const [userDataStatus, setUserDataStatus] = useState<'loading' | 'complete' | 'incomplete' | 'error'>('loading');
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const { user } = useUser();
  const { schools } = useSchools();
  const { prerequisites, loading: prerequisitesLoading } = usePrerequisites();
  const { coursePrerequisites, loading: coursePrerequisitesLoading } = useCoursePrerequisites();

  // Helper functions for persistent caching
  const saveEligibilityCacheToStorage = (cache: { [programId: string]: EligibilityResult }, cacheKey: string) => {
    try {
      const cacheData = {
        cache,
        cacheKey,
        timestamp: Date.now(),
        userUid: user?.uid
      };
      localStorage.setItem('eligibilityCache', JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save eligibility cache to localStorage:', error);
    }
  };

  const loadEligibilityCacheFromStorage = (): { cache: { [programId: string]: EligibilityResult }, cacheKey: string } | null => {
    try {
      const cached = localStorage.getItem('eligibilityCache');
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      
      // Check if cache is for the current user and not too old (7 days)
      const isCurrentUser = cacheData.userUid === user?.uid;
      const isNotExpired = Date.now() - cacheData.timestamp < 7 * 24 * 60 * 60 * 1000; // 7 days
      
      if (isCurrentUser && isNotExpired) {
        return {
          cache: cacheData.cache,
          cacheKey: cacheData.cacheKey
        };
      }
      
      // Clear expired or invalid cache
      localStorage.removeItem('eligibilityCache');
      return null;
    } catch (error) {
      console.warn('Failed to load eligibility cache from localStorage:', error);
      localStorage.removeItem('eligibilityCache');
      return null;
    }
  };

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const db = getDatabase();
      
      // Fetch both programs and schools data
      const [programsSnapshot, schoolsSnapshot] = await Promise.all([
        get(ref(db, 'programs')),
        get(ref(db, 'schools'))
      ]);

      if (programsSnapshot.exists()) {
        const programsData = programsSnapshot.val();
        const schoolsData = schoolsSnapshot.exists() ? schoolsSnapshot.val() : {};
        
        // Create a set of active school names (schools that haven't been deleted)
        const activeSchoolNames = new Set<string>();
        Object.entries(schoolsData).forEach(([id, data]: [string, any]) => {
          if (!data.SchoolLastDeleted) {
            activeSchoolNames.add(data.SchoolName || '');
          }
        });

        const programsArray: Program[] = Object.entries(programsData)
          .filter(([id, data]: [string, any]) => {
            // Filter out programs that have been archived (have a timestamp in ProgramLastDeleted)
            if (data.ProgramLastDeleted) {
              return false;
            }
            
            // Filter out programs whose associated school has been deleted
            const schoolName = data.ProgramSchoolName || '';
            if (!activeSchoolNames.has(schoolName)) {
              return false;
            }
            
            return true;
          })
          .map(([id, data]: [string, any]) => ({
            id,
            program: data.ProgramName || '',
            programCode: data.ProgramCode || '',
            estimatedMinimum: data.ProgramAdmissionsMinimum || '',
            prerequisites: data.ProgramPrerequisites || '',
            experientialLearning: data.ProgramExperientialLearning || '',
            province: data.ProgramProvinceState || '',
            category: data.ProgramCategory || '',
            description: data.ProgramDescription || '',
            degree: data.ProgramDegree || '',
            language: data.ProgramLanguage || '',
            domesticTuition: data.ProgramDomesticTuition || '',
            internationalTuition: data.ProgramInternationalTuition || '',
            additionalRequirements: data.ProgramAdditionalRequirements || '',
            url: data.ProgramURL || '',
            schoolName: data.ProgramSchoolName || '',
            ProgramLastDeleted: data.ProgramLastDeleted || undefined,
          }));
        

        
        setPrograms(programsArray);
      } else {
        setPrograms([]);
      }
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Failed to fetch programs data');
    } finally {
      setLoading(false);
    }
  };

  const checkEligibilityForSchool = async (schoolID: string) => {
    if (!user) {
      setEligibilityErrorWithLog('User must be logged in to check eligibility');
      return;
    }

    // Check if prerequisites and course prerequisites are still loading
    if (prerequisitesLoading || coursePrerequisitesLoading) {
      return;
    }

    // Wait for user courses to be loaded (race condition protection)
    if (!user.courses || Object.keys(user.courses).length === 0) {
      console.log('⏸️ checkEligibilityForSchool: User courses not loaded yet, skipping calculation');
      return;
    }

    // Prevent duplicate calculations
    if (eligibilityCalculationLock) {
      console.log('🔄 Eligibility calculation already in progress, skipping duplicate school check');
      return;
    }

    try {
      setEligibilityCalculationLock(true);
      setEligibilityLoading(true);
      setEligibilityErrorWithLog(null);

      // Derive grade from courses
      let derivedGrade = '10'; // Default to Grade 10
      const hasGrade12Courses = Object.values(user.courses).some((course: any) => 
        course.grade === '12' || course.grades === 12
      );
      const hasGrade11Courses = Object.values(user.courses).some((course: any) => 
        course.grade === '11' || course.grades === 11
      );
      
      if (hasGrade12Courses) {
        derivedGrade = '12';
      } else if (hasGrade11Courses) {
        derivedGrade = '11';
      }
      // If no Grade 11 or Grade 12 courses found, keep as Grade 10


      // Filter programs to only include those for the current school
      const currentSchool = schools.find(s => s.id === schoolID);
      const schoolName = currentSchool?.name || "Brock University"; // Fallback for Brock University
      
      const schoolPrograms = programs.filter(program => {
        return program.schoolName === schoolName;
      });
      

      // Calculate eligibility using hybrid approach (only for this school's programs)
      let eligibilityResults: { [programId: string]: EligibilityResult };
      try {
        // For school-specific calculations, we need to get all programs and filter
        // Firebase-only architecture: Use cache data instead of hybrid calculation
        console.log('checkEligibilityForSchool: Using Firebase cache data');
        const allEligibilityResults = globalEligibilityCache;
        
        // Filter results to only include this school's programs
        eligibilityResults = {};
        for (const program of schoolPrograms) {
          if (allEligibilityResults[program.id]) {
            eligibilityResults[program.id] = allEligibilityResults[program.id];
          }
        }
        
        // Log the calculation type (Firebase-only architecture)
        try {
          await activityLogService.logEligibilityCalculation(user.uid, 'API');
        } catch (error) {
          console.error('Error logging eligibility calculation activity:', error);
          // Don't block the flow if logging fails
        }
      } catch (error) {
        console.error('Error in Firebase cache access for school:', error);
        // No fallback needed in Firebase-only architecture
        eligibilityResults = {};
        
        // Log as local calculation since we fell back
        try {
          await activityLogService.logEligibilityCalculation(user.uid, 'Local');
        } catch (logError) {
          console.error('Error logging fallback calculation activity:', logError);
        }
      }
      
      // Update programs with eligibility results
      const updatedPrograms = programs.map(program => {
        const eligibilityResult = eligibilityResults[program.id];
        if (eligibilityResult) {
          return {
            ...program,
            eligibility: {
              isEligible: eligibilityResult.ifEligible,
              alertMessage: eligibilityResult.alertMessage,
              nextYearSuggestion: eligibilityResult.nextYearSuggestion,
              calculatedAverage: eligibilityResult.calculatedAverage,
              programAverage: eligibilityResult.programAverage,
              missingCourses: eligibilityResult.missingCourses,
              gradeIssues: eligibilityResult.gradeIssues,
              coursesUsedInAverage: eligibilityResult.coursesUsedInAverage
            }
          };
        }
        return program;
      });
      
      setPrograms(updatedPrograms);

      // Store the results for reference
      setEligibilityData({
        calcResult: eligibilityResults,
        grade: derivedGrade,
        courses: user.courses,
        schoolsAndPrograms: programs,
        name: `${user.firstName || ''} ${user.lastName || ''}`,
        hs: user.schoolName || '',
        prereqs: {},
        userData: user
      });

    } catch (err: any) {
      console.error('Local eligibility calculation error:', err);
      setEligibilityErrorWithLog('Failed to calculate eligibility: ' + (err.message || 'Unknown error'));
    } finally {
      setEligibilityLoading(false);
    }
  };

  const clearEligibility = () => {
    setPrograms(prevPrograms => 
      prevPrograms.map(program => ({
        ...program,
        eligibility: undefined // Set to undefined to clear the eligibility data
      }))
    );
    setEligibilityErrorWithLog(null);
    setEligibilityData(null);
    setGlobalEligibilityCache({});
    setEligibilityCacheKey('');
    setLastUserCoursesHash('');
    setCacheLoaded(false);
    // Clear localStorage cache as well
    localStorage.removeItem('eligibilityCache');
  };

  // New efficient eligibility calculation for all programs
  const calculateAllEligibility = async () => {
    if (!user) {
      setEligibilityErrorWithLog('User must be logged in to check eligibility');
      return;
    }

    // Check if prerequisites are still loading
    if (prerequisitesLoading) {
      return;
    }

    // Wait for user courses to be loaded (race condition protection)
    if (!user.courses || Object.keys(user.courses).length === 0) {
      console.log('⏸️ calculateAllEligibility: User courses not loaded yet, skipping calculation');
      return;
    }

    // Create a cache key based on user data to determine if we need to recalculate
    const userCoursesHash = JSON.stringify(user.courses);
    const newCacheKey = `${user.uid}-${userCoursesHash}`;
    
    // If we already have cached results for this user data, don't recalculate
    if (eligibilityCacheKey === newCacheKey && Object.keys(globalEligibilityCache).length > 0) {
      return;
    }

    try {
      setEligibilityLoading(true);
      setEligibilityErrorWithLog(null);


      // Derive grade from courses
      let derivedGrade = '10'; // Default to Grade 10
      const hasGrade12Courses = Object.values(user.courses).some((course: any) => 
        course.grade === '12' || course.grades === 12
      );
      const hasGrade11Courses = Object.values(user.courses).some((course: any) => 
        course.grade === '11' || course.grades === 11
      );
      
      if (hasGrade12Courses) {
        derivedGrade = '12';
      } else if (hasGrade11Courses) {
        derivedGrade = '11';
      }
      // If no Grade 11 or Grade 12 courses found, keep as Grade 10


      // Firebase-only architecture: Use cache data instead of local calculation
      console.log('calculateAllEligibility: Using Firebase cache data');
      const eligibilityResults = globalEligibilityCache;
      
      // Cache the results
      setGlobalEligibilityCache(eligibilityResults);
      setEligibilityCacheKey(newCacheKey);

      // Update programs with eligibility results
      const updatedPrograms = programs.map(program => {
        const eligibilityResult = eligibilityResults[program.id];
        if (eligibilityResult) {
          return {
            ...program,
            eligibility: {
              isEligible: eligibilityResult.ifEligible,
              alertMessage: eligibilityResult.alertMessage,
              nextYearSuggestion: eligibilityResult.nextYearSuggestion,
              calculatedAverage: eligibilityResult.calculatedAverage,
              programAverage: eligibilityResult.programAverage,
              missingCourses: eligibilityResult.missingCourses,
              gradeIssues: eligibilityResult.gradeIssues,
              coursesUsedInAverage: eligibilityResult.coursesUsedInAverage,
              coursesWithGradeIssues: eligibilityResult.coursesWithGradeIssues
            }
          };
        }
        return program;
      });
      
      setPrograms(updatedPrograms);

      // Store the results for reference
      setEligibilityData({
        calcResult: eligibilityResults,
        grade: derivedGrade,
        courses: user.courses,
        schoolsAndPrograms: programs,
        name: `${user.firstName || ''} ${user.lastName || ''}`,
        hs: user.schoolName || '',
        prereqs: {},
        userData: user
      });


    } catch (err: any) {
      console.error('Global eligibility calculation error:', err);
      setEligibilityErrorWithLog('Failed to calculate eligibility: ' + (err.message || 'Unknown error'));
    } finally {
      setEligibilityLoading(false);
    }
  };

  // Check if eligibility has been calculated for current user data
  const isEligibilityCalculated = () => {
    if (!user) return false;
    const userCoursesHash = JSON.stringify(user.courses);
    const currentCacheKey = `${user.uid}-${userCoursesHash}`;
    return eligibilityCacheKey === currentCacheKey && Object.keys(globalEligibilityCache).length > 0;
  };

  // Check if eligibility has been calculated for specific schools
  const isEligibilityCalculatedForSchools = useCallback((schoolNames: string[]): boolean => {
    if (!user) return false;
    
    const programsForSchools = programs.filter(program => schoolNames.includes(program.schoolName));
    const allProgramsHaveCache = programsForSchools.every(program => globalEligibilityCache[program.id]);
    const allProgramsHaveEligibility = programsForSchools.every(program => program.eligibility);
    
    // Return true if either cache or direct eligibility data is available
    const result = allProgramsHaveCache || allProgramsHaveEligibility;
    return result;
  }, [user, programs, globalEligibilityCache]);

  // Calculate eligibility for specific schools only
  const calculateEligibilityForSchools = useCallback(async (schoolNames: string[]) => {
    if (!user) {
      setEligibilityErrorWithLog('User must be logged in to check eligibility');
      return;
    }

    // Check if prerequisites are still loading
    if (prerequisitesLoading) {
      return;
    }

    // Wait for user courses to be loaded (race condition protection)
    if (!user.courses || Object.keys(user.courses).length === 0) {
      return;
    }

    // Check if we already calculated for these schools
    const schoolsAlreadyCalculated = schoolNames.every(schoolName => 
      isEligibilityCalculatedForSchools([schoolName])
    );
    
    if (schoolsAlreadyCalculated) {
      return;
    }

    try {
      setEligibilityCalculationInProgress(true);
      setEligibilityErrorWithLog(null);


      // Derive grade from courses
      let derivedGrade = '10'; // Default to Grade 10
      const hasGrade12Courses = Object.values(user.courses).some((course: any) => 
        course.grade === '12' || course.grades === 12
      );
      const hasGrade11Courses = Object.values(user.courses).some((course: any) => 
        course.grade === '11' || course.grades === 11
      );
      
      if (hasGrade12Courses) {
        derivedGrade = '12';
      } else if (hasGrade11Courses) {
        derivedGrade = '11';
      }
      // If no Grade 11 or Grade 12 courses found, keep as Grade 10


      // Filter programs to only include those for the specified schools
      const schoolPrograms = programs.filter(program => 
        schoolNames.includes(program.schoolName)
      );
      

      // Firebase-only architecture: Use cache data instead of local calculation
      console.log('calculateEligibilityForSchools: Using Firebase cache data');
      const eligibilityResults = globalEligibilityCache;
      
      // Update global cache with new results
      setGlobalEligibilityCache(prevCache => ({
        ...prevCache,
        ...eligibilityResults
      }));

      // Update programs with eligibility results
      const updatedPrograms = programs.map(program => {
        const eligibilityResult = eligibilityResults[program.id];
        if (eligibilityResult) {
          return {
            ...program,
            eligibility: {
              isEligible: eligibilityResult.ifEligible,
              alertMessage: eligibilityResult.alertMessage,
              nextYearSuggestion: eligibilityResult.nextYearSuggestion,
              calculatedAverage: eligibilityResult.calculatedAverage,
              programAverage: eligibilityResult.programAverage,
              missingCourses: eligibilityResult.missingCourses,
              gradeIssues: eligibilityResult.gradeIssues,
              coursesUsedInAverage: eligibilityResult.coursesUsedInAverage,
              coursesWithGradeIssues: eligibilityResult.coursesWithGradeIssues
            }
          };
        }
        return program;
      });
      
      setPrograms(updatedPrograms);


    } catch (err: any) {
      console.error('Progressive eligibility calculation error:', err);
      setEligibilityErrorWithLog('Failed to calculate eligibility: ' + (err.message || 'Unknown error'));
    } finally {
      setEligibilityCalculationInProgress(false);
      setEligibilityCalculationLock(false);
    }
  }, [user, prerequisitesLoading, programs, globalEligibilityCache, isEligibilityCalculatedForSchools]);

  // Calculate eligibility for all remaining programs
  const calculateEligibilityForAllPrograms = async () => {
    if (!user) {
      setEligibilityErrorWithLog('User must be logged in to check eligibility');
      return;
    }

    // Check if prerequisites are still loading
    if (prerequisitesLoading) {
      return;
    }

    // Wait for user courses to be loaded (race condition protection)
    if (!user.courses || Object.keys(user.courses).length === 0) {
      return;
    }

    // Check if we already calculated all eligibility
    if (isEligibilityCalculated()) {
      return;
    }

    // Prevent duplicate calculations
    if (eligibilityCalculationLock) {
      console.log('🔄 Eligibility calculation already in progress, skipping duplicate request');
      return;
    }

    try {
      setEligibilityCalculationLock(true);
      setEligibilityCalculationInProgress(true);
      setEligibilityErrorWithLog(null);


      // Derive grade from courses
      let derivedGrade = '10'; // Default to Grade 10
      const hasGrade12Courses = Object.values(user.courses).some((course: any) => 
        course.grade === '12' || course.grades === 12
      );
      const hasGrade11Courses = Object.values(user.courses).some((course: any) => 
        course.grade === '11' || course.grades === 11
      );
      
      if (hasGrade12Courses) {
        derivedGrade = '12';
      } else if (hasGrade11Courses) {
        derivedGrade = '11';
      }
      // If no Grade 11 or Grade 12 courses found, keep as Grade 10


      // Firebase-only architecture: Use cache data instead of hybrid calculation
      console.log('calculateEligibilityForAllPrograms: Using Firebase cache data');
      const eligibilityResults = globalEligibilityCache;
      
      // Log the calculation type (Firebase-only architecture)
      try {
        await activityLogService.logEligibilityCalculation(user.uid, 'API');
      } catch (error) {
        console.error('Error logging eligibility calculation activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Cache the results
      setGlobalEligibilityCache(eligibilityResults);
      setEligibilityCacheKey(`${user.uid}-${JSON.stringify(user.courses)}`);

      // Update programs with eligibility results
      const updatedPrograms = programs.map(program => {
        const eligibilityResult = eligibilityResults[program.id];
        if (eligibilityResult) {
          return {
            ...program,
            eligibility: {
              isEligible: eligibilityResult.ifEligible,
              alertMessage: eligibilityResult.alertMessage,
              nextYearSuggestion: eligibilityResult.nextYearSuggestion,
              calculatedAverage: eligibilityResult.calculatedAverage,
              programAverage: eligibilityResult.programAverage,
              missingCourses: eligibilityResult.missingCourses,
              gradeIssues: eligibilityResult.gradeIssues,
              coursesUsedInAverage: eligibilityResult.coursesUsedInAverage,
              coursesWithGradeIssues: eligibilityResult.coursesWithGradeIssues
            }
          };
        }
        return program;
      });
      
      setPrograms(updatedPrograms);

      // Store the results for reference
      setEligibilityData({
        calcResult: eligibilityResults,
        grade: derivedGrade,
        courses: user.courses,
        schoolsAndPrograms: programs,
        name: `${user.firstName || ''} ${user.lastName || ''}`,
        hs: user.schoolName || '',
        prereqs: {},
        userData: user
      });


    } catch (err: any) {
      console.error('Complete eligibility calculation error:', err);
      setEligibilityErrorWithLog('Failed to calculate eligibility: ' + (err.message || 'Unknown error'));
    } finally {
      setEligibilityCalculationInProgress(false);
      setEligibilityCalculationLock(false);
    }
  };

  // Get eligibility for a specific program from cache
  const getProgramEligibility = (programId: string): EligibilityResult | null => {
    return globalEligibilityCache[programId] || null;
  };




  // Refresh eligibility if prerequisites have changed
  const refreshEligibilityIfNeeded = async () => {
    if (!user || !prerequisites || eligibilityCalculationInProgress) {
      return;
    }

    // Create a hash of prerequisites to detect changes
    const prerequisitesHash = JSON.stringify(prerequisites);
    
    // If prerequisites haven't changed, no need to refresh
    if (lastPrerequisitesHash === prerequisitesHash) {
      return;
    }

    setLastPrerequisitesHash(prerequisitesHash);
    setGlobalEligibilityCache({});
    setEligibilityCacheKey('');
    
    // Clear program eligibility data so UI shows loading state instead of old data
    setPrograms(prevPrograms => 
      prevPrograms.map(program => ({
        ...program,
        eligibility: undefined
      }))
    );
    
    // Recalculate eligibility for all currently cached programs
    const cachedProgramIds = Object.keys(globalEligibilityCache);
    if (cachedProgramIds.length > 0) {
      const cachedPrograms = programs.filter(program => cachedProgramIds.includes(program.id));
      const schoolNames = Array.from(new Set(cachedPrograms.map(program => program.schoolName)));
      await calculateEligibilityForSchools(schoolNames);
    }
  };

  const refreshPrograms = () => {
    fetchPrograms();
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  // Load eligibility cache from localStorage when user and programs are available
  useEffect(() => {
    if (user?.uid && programs.length > 0 && !loading && !cacheLoaded && !prerequisitesLoading) {
      const cachedData = loadEligibilityCacheFromStorage();
      if (cachedData) {
        // Validate that the cache is still valid for current user courses
        const currentCoursesHash = JSON.stringify(user.courses || {});
        const expectedCacheKey = `${user.uid}-${currentCoursesHash}`;
        
        if (cachedData.cacheKey === expectedCacheKey) {
          setGlobalEligibilityCache(cachedData.cache);
          setEligibilityCacheKey(cachedData.cacheKey);
          setCacheLoaded(true);
          console.log('📦 Loaded valid eligibility cache from localStorage:', Object.keys(cachedData.cache).length, 'programs');
          
          // Update programs with cached eligibility data
          setPrograms(prevPrograms => 
            prevPrograms.map(program => {
              const eligibilityResult = cachedData.cache[program.id];
              if (eligibilityResult) {
                return {
                  ...program,
                  eligibility: {
                    isEligible: eligibilityResult.ifEligible,
                    alertMessage: eligibilityResult.alertMessage,
                    nextYearSuggestion: eligibilityResult.nextYearSuggestion,
                    calculatedAverage: eligibilityResult.calculatedAverage,
                    programAverage: eligibilityResult.programAverage,
                    missingCourses: eligibilityResult.missingCourses,
                    gradeIssues: eligibilityResult.gradeIssues,
                    coursesUsedInAverage: eligibilityResult.coursesUsedInAverage,
                    coursesWithGradeIssues: eligibilityResult.coursesWithGradeIssues
                  }
                };
              }
              return program;
            })
          );
        } else {
          console.log('📦 Eligibility cache is invalid for current user courses, clearing cache');
          localStorage.removeItem('eligibilityCache');
          setCacheLoaded(true);
        }
      } else {
        setCacheLoaded(true); // Mark as loaded even if no cache found
      }
    }
  }, [user?.uid, programs.length, loading, cacheLoaded, prerequisitesLoading, user?.courses]);

  // Save eligibility cache to localStorage when it changes
  useEffect(() => {
    if (Object.keys(globalEligibilityCache).length > 0 && eligibilityCacheKey && user?.uid) {
      saveEligibilityCacheToStorage(globalEligibilityCache, eligibilityCacheKey);
      console.log('💾 Saved eligibility cache to localStorage:', Object.keys(globalEligibilityCache).length, 'programs');
    }
  }, [globalEligibilityCache, eligibilityCacheKey, user?.uid]);

  // DISABLED: Auto-calculate eligibility when user data changes (with safeguards)
  // This was causing race conditions with manual save button triggers
  // The manual save button in UserStudentProfile now handles cache clearing
  useEffect(() => {
    if (!user?.uid || !user?.courses || Object.keys(user.courses).length === 0) {
      return;
    }
    
    console.log('⏸️ ProgramsContext: User courses changed, but auto-cache clearing is disabled');
    console.log('⏸️ ProgramsContext: Manual save button will handle cache clearing and API calls');
    
    // Just update the hash without clearing cache
    const currentCoursesHash = JSON.stringify(user.courses);
    if (lastUserCoursesHash === '') {
      console.log('🔄 ProgramsContext: First time loading user courses, initializing hash');
      setLastUserCoursesHash(currentCoursesHash);
    } else if (lastUserCoursesHash !== currentCoursesHash) {
      console.log('🔄 ProgramsContext: User courses changed, updating hash (cache clearing disabled)');
      setLastUserCoursesHash(currentCoursesHash);
    }
  }, [user?.uid, user?.courses, lastUserCoursesHash]);

  // DISABLED: Monitor prerequisite changes and refresh eligibility if needed
  // This was causing infinite re-render loops that prevented save button clicks
  // useEffect(() => {
  //   if (!prerequisites || Object.keys(prerequisites).length === 0) {
  //     console.log('⏸️ ProgramsContext: No prerequisites loaded yet, skipping');
  //     return;
  //   }
  //   
  //   console.log('⏸️ ProgramsContext: Prerequisites changed, but auto-refresh is disabled');
  //   console.log('⏸️ ProgramsContext: Manual save button will handle eligibility refresh');
  //   
  //   // Just update the hash without triggering refresh
  //   const currentPrerequisitesHash = JSON.stringify(prerequisites);
  //   if (currentPrerequisitesHash === lastPrerequisitesHash) {
  //     console.log('⏸️ ProgramsContext: Prerequisites unchanged, skipping refresh');
  //     return;
  //   }
  //   
  //   console.log('⏸️ ProgramsContext: Prerequisites changed, updating hash (auto-refresh disabled)');
  //   setLastPrerequisitesHash(currentPrerequisitesHash);
  // }, [prerequisites, lastPrerequisitesHash, user?.courses]);

  // DISABLED: Watch for coursePrerequisites changes and refresh eligibility only if actually changed
  // This was causing infinite re-render loops that prevented save button clicks
  // useEffect(() => {
  //   if (!coursePrerequisites || Object.keys(coursePrerequisites).length === 0) {
  //     console.log('⏸️ ProgramsContext: No coursePrerequisites loaded yet, skipping');
  //     return;
  //   }
  //   
  //   console.log('⏸️ ProgramsContext: CoursePrerequisites changed, but auto-refresh is disabled');
  //   console.log('⏸️ ProgramsContext: Manual save button will handle eligibility refresh');
  //   
  //   // Just update the hash without triggering refresh
  //   const currentCoursePrerequisitesHash = JSON.stringify(coursePrerequisites);
  //   if (currentCoursePrerequisitesHash === lastPrerequisitesHash) {
  //     console.log('⏸️ ProgramsContext: CoursePrerequisites unchanged, skipping refresh');
  //     return;
  //   }
  //   
  //   console.log('⏸️ ProgramsContext: CoursePrerequisites changed, updating hash (auto-refresh disabled)');
  //   setLastPrerequisitesHash(currentCoursePrerequisitesHash);
  // }, [coursePrerequisites, lastPrerequisitesHash, user?.courses]);

  // Phase 6: Cache Listener - Re-enable with safeguards
  useEffect(() => {
    if (!user?.uid) {
      console.log('⏸️ ProgramsContext: No user UID, skipping Firebase cache setup');
      setUserDataStatus('loading');
      return;
    }
    
    // Wait for user courses to be loaded before setting up listener
    if (!user.courses || Object.keys(user.courses).length === 0) {
      console.log('⏸️ ProgramsContext: User courses not loaded yet, waiting...');
      setUserDataStatus('loading');
      return;
    }
    
    console.log('✅ ProgramsContext: User courses loaded, setting up Firebase metadata listener');
    console.log('📊 ProgramsContext: User has', Object.keys(user.courses).length, 'courses');
    
    // Clear any existing errors when user courses are loaded
    console.log('🧹 ProgramsContext: Clearing errors now that user courses are loaded');
    setEligibilityErrorWithLog(null);
    setCacheError(null);
    setError(null);
    
    // Set up Firebase metadata listener with timeout
    const db = getDatabase();
    const metadataRef = ref(db, `eligibilityCacheMetadata/${user.uid}`);
    
      console.log('🔄 ProgramsContext: Setting up metadata listener for user:', user.uid);
      console.log('🔍 ProgramsContext: Listening to path: eligibilityCacheMetadata/' + user.uid);
      
      const unsubscribe = onValue(metadataRef, (snapshot) => {
        console.log('📡 ProgramsContext: Metadata listener triggered');
        console.log('📊 ProgramsContext: Snapshot exists:', snapshot.exists());
        console.log('📊 ProgramsContext: Snapshot value:', snapshot.val());
        
        if (snapshot.exists()) {
          const metadata = snapshot.val();
          console.log('📊 ProgramsContext: Metadata received:', metadata);
        
        // Always try to load cache first, regardless of migration status
        console.log('🔄 ProgramsContext: Attempting to load cache for user:', user.uid);
        const cacheRef = ref(db, `eligibilityCache/${user.uid}`);
        get(cacheRef).then((cacheSnapshot) => {
          if (cacheSnapshot.exists()) {
            const cacheData = cacheSnapshot.val();
            console.log('✅ ProgramsContext: Cache found with', Object.keys(cacheData).length, 'programs');
            
            // Update state with cache data
            console.log('🔄 ProgramsContext: Updating state with cache data');
            setGlobalEligibilityCache(cacheData);
            setCacheLoading(false);
            setUserDataStatus('complete');
            setCacheError(null);
            setIsCalculating(false);
            
            console.log('✅ ProgramsContext: Cache loaded successfully');
          } else {
            console.log('❌ ProgramsContext: No cache found, checking migration status');
            
            if (metadata.migrated && metadata.programCount > 0) {
              console.log('❌ ProgramsContext: User is migrated but cache not found');
              setCacheError('Cache not found despite migration');
              setUserDataStatus('error');
            } else {
              console.log('🔄 ProgramsContext: User not migrated, triggering API calculation');
              console.log('📊 ProgramsContext: Metadata exists but user not migrated:', metadata);
              setUserDataStatus('incomplete');
              triggerEligibilityCalculation();
            }
          }
        }).catch((error) => {
          console.error('❌ ProgramsContext: Error loading cache:', error);
          setCacheError('Failed to load cache');
          setUserDataStatus('error');
        });
      } else {
        console.log('🔄 ProgramsContext: No metadata found, triggering API calculation');
        console.log('❌ ProgramsContext: Metadata snapshot does not exist for user:', user.uid);
        console.log('🔍 ProgramsContext: Current cache size before API call:', Object.keys(globalEligibilityCache).length);
        setUserDataStatus('incomplete');
        triggerEligibilityCalculation();
      }
    }, (error) => {
      console.error('❌ ProgramsContext: Metadata listener error:', error);
      setCacheError('Failed to connect to Firebase');
      setUserDataStatus('error');
    });
    
    return () => {
      console.log('🔄 ProgramsContext: Cleaning up metadata listener');
      unsubscribe();
    };
  }, [user?.uid, user?.courses]);

  // Check if user has required data for API calculation
  const checkUserDataStatus = async (userId: string): Promise<{ hasRequiredData: boolean }> => {
    try {
      console.log('🔍 Checking user data status for:', userId);
      
      // First check if we have user data in context (faster)
      if (user?.courses && Object.keys(user.courses).length > 0) {
        console.log('✅ User courses available in context, checking grades...');
        
        // Check for at least one course with grades > 0
        let hasAnyGrades = false;
        for (const [, course] of Object.entries(user.courses)) {
          if (course && typeof course === 'object' && 'grades' in course && course.grades && typeof course.grades === 'number' && course.grades > 0) {
            hasAnyGrades = true;
            break;
          }
        }
        
        if (hasAnyGrades) {
          console.log('✅ User has sufficient data for API calculation (from context)');
          return { hasRequiredData: true };
        }
      }
      
      // Fallback to database check if context data not available
      console.log('🔍 User courses not in context, checking database...');
      const db = getDatabase();
      const userRef = ref(db, `user/${userId}`);
      const userSnapshot = await get(userRef);
      
      if (!userSnapshot.exists()) {
        console.log('❌ User not found in database');
        return { hasRequiredData: false };
      }
      
      const userData = userSnapshot.val();
      
      // Check for ProfileData
      if (!userData.ProfileData) {
        console.log('❌ User missing ProfileData');
        return { hasRequiredData: false };
      }
      
      // Check for Current Grade
      const userGrade = userData.ProfileData["Current Grade"] || userData.ProfileData["Grade"];
      if (!userGrade || !["10", "11", "12"].includes(userGrade)) {
        console.log('❌ User missing valid grade');
        return { hasRequiredData: false };
      }
      
      // Check for courses with grades (prioritize user.grades over ProfileData)
      const userCourses = userData.grades || userData.ProfileData["Courses"] || userData.ProfileData["Student Courses"] || {};
      
      if (Object.keys(userCourses).length === 0) {
        console.log('❌ User missing courses data');
        return { hasRequiredData: false };
      }
      
      // Check for at least one course with grades > 0
      let hasAnyGrades = false;
      for (const [, course] of Object.entries(userCourses)) {
        if (course && typeof course === 'object' && 'grades' in course && course.grades && typeof course.grades === 'number' && course.grades > 0) {
          hasAnyGrades = true;
          break;
        }
      }
      
      if (!hasAnyGrades) {
        console.log('❌ User has no courses with grades');
        return { hasRequiredData: false };
      }
      
      console.log('✅ User has sufficient data for API calculation (from database)');
      return { hasRequiredData: true };
    } catch (error) {
      console.error('❌ Error checking user data status:', error);
      return { hasRequiredData: false };
    }
  };

  // Add debouncing to prevent multiple rapid API calls
  const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
  const [apiCallTimeout, setApiCallTimeout] = useState<NodeJS.Timeout | null>(null);

  // Trigger API calculation for non-migrated users with data
  const triggerEligibilityCalculation = async () => {
    if (!user?.uid) {
      console.error('❌ Cannot trigger eligibility calculation: user not available');
      setCacheError('User not available for eligibility calculation');
      setCacheLoading(false);
      setUserDataStatus('error');
      return;
    }
    
    // Prevent multiple simultaneous API calls
    if (isApiCallInProgress) {
      console.log('⏸️ ProgramsContext: API call already in progress, skipping duplicate request');
      return;
    }
    
    // Clear any existing timeout
    if (apiCallTimeout) {
      clearTimeout(apiCallTimeout);
    }
    
    // Debounce API calls - wait 2 seconds before making the call
    console.log('⏸️ ProgramsContext: Debouncing API call for 2 seconds to prevent rapid-fire requests');
    const timeout = setTimeout(async () => {
      await executeApiCall();
    }, 2000);
    
    setApiCallTimeout(timeout);
  };
  
  const executeApiCall = async () => {
    if (isApiCallInProgress) {
      console.log('⏸️ ProgramsContext: API call already in progress, skipping');
      return;
    }
    
    setIsApiCallInProgress(true);
    
    // Check if we already have cached data - if so, don't trigger calculation
    if (Object.keys(globalEligibilityCache).length > 0) {
      console.log('⚠️ ProgramsContext: Cache already loaded with', Object.keys(globalEligibilityCache).length, 'programs');
      console.log('🔄 ProgramsContext: But proceeding with API calculation as requested');
      // Don't return - continue with the API call
    }
    
    // Don't automatically trigger calculations on page load - only when explicitly requested
    console.log('🔄 ProgramsContext: Proceeding with eligibility calculation as requested');
    
    try {
      console.log('🚀 Triggering API eligibility calculation for user:', user?.uid);
      console.log('🔍 ProgramsContext: Current cache size:', Object.keys(globalEligibilityCache).length);
      setCacheLoading(true);
      setCacheError(null);
      setEligibilityErrorWithLog(null); // Clear any previous eligibility errors
      setIsCalculating(true);
      
      const result = await eligibilityRefreshService.startRefresh();
      
      if (result.success) {
        console.log('✅ Eligibility calculation triggered successfully');
        // Firebase listener will pick up the result when calculation completes
        // Don't set isCalculating to false here - let the cache listener handle it
      } else {
        throw new Error(result.error || 'API calculation failed');
      }
    } catch (error) {
      console.error('❌ Error triggering eligibility calculation:', error);
      setCacheError('Failed to calculate eligibility. Please try again later.');
      // Don't set eligibilityError for Firebase function errors - just log them
      console.log('⚠️ ProgramsContext: Firebase function error, not setting eligibilityError');
      setCacheLoading(false);
      setUserDataStatus('error');
      setIsCalculating(false);
    } finally {
      setIsApiCallInProgress(false);
    }
  };

  // Handle onboarding completion
  const handleOnboardingComplete = async () => {
    console.log('🎉 Onboarding completed - triggering eligibility calculation');
    setShowOnboardingModal(false);
    setUserDataStatus('complete');
    
    // Trigger API calculation after onboarding
    await triggerEligibilityCalculation();
  };

  // Clear all error states
  const clearErrors = () => {
    console.log('🧹 Clearing all error states');
    setEligibilityErrorWithLog(null);
    setCacheError(null);
    setError(null);
  };

  // Force clear eligibility error specifically
  const clearEligibilityError = () => {
    console.log('🧹 Force clearing eligibility error');
    setEligibilityErrorWithLog(null);
  };

  // Force clear all error states
  const forceClearAllErrors = () => {
    console.log('🧹 Force clearing ALL error states');
    setEligibilityErrorWithLog(null);
    setCacheError(null);
    setError(null);
    
    // Also clear localStorage cache to remove any cached error states
    try {
      localStorage.removeItem('eligibilityCache');
      console.log('🧹 Cleared localStorage eligibilityCache');
    } catch (error) {
      console.warn('Failed to clear localStorage cache:', error);
    }
  };

  // Make forceClearAllErrors available globally for debugging
  if (typeof window !== 'undefined') {
    (window as any).clearAllErrors = forceClearAllErrors;
  }

  // AGGRESSIVE ERROR CLEARING - DISABLED to prevent interference with snackbar
  // useEffect(() => {
  //   if (user?.courses && Object.keys(user.courses).length > 0) {
  //     // User has courses, clear any eligibility errors
  //     if (eligibilityError) {
  //       console.log('🧹 AGGRESSIVE: Clearing eligibilityError because user has courses');
  //       setEligibilityErrorWithLog(null);
  //     }
  //   }
  // }, [user?.courses, eligibilityError]);

  // Force clear Firebase cache and errors (for manual error clearing)
  const forceClearCacheAndErrors = async () => {
    if (!user?.uid) {
      console.log('❌ Cannot clear cache: user not available');
      return;
    }
    
    try {
      console.log('🧹 Force clearing Firebase cache and errors');
      
      // Clear Firebase cache and metadata
      const db = getDatabase();
      const cacheRef = ref(db, `eligibilityCache/${user.uid}`);
      const metadataRef = ref(db, `eligibilityCacheMetadata/${user.uid}`);
      
      await Promise.all([
        remove(cacheRef),
        remove(metadataRef)
      ]);
      
      // Clear all error states
      setEligibilityErrorWithLog(null);
      setCacheError(null);
      setError(null);
      
      console.log('✅ Firebase cache and errors cleared successfully');
    } catch (error) {
      console.error('❌ Error force clearing cache:', error);
    }
  };

  // Export eligibility cache to Firebase
  const exportEligibilityCacheToFirebase = async (): Promise<void> => {
    if (!user?.uid || Object.keys(globalEligibilityCache).length === 0) {
      console.log('📭 No eligibility cache to export or user not available');
      return;
    }

    try {
      const { bulkExportEligibilityCache } = await import('../utils/eligibilityCacheExporter');
      await bulkExportEligibilityCache([{
        userId: user.uid,
        eligibilityCache: globalEligibilityCache,
        cacheKey: eligibilityCacheKey || 'manual_export'
      }]);
      console.log('✅ Successfully exported eligibility cache to Firebase:', Object.keys(globalEligibilityCache).length, 'programs');
    } catch (error) {
      console.error('❌ Failed to export eligibility cache to Firebase:', error);
      throw error;
    }
  };

  const value: ProgramsContextType = {
    programs,
    loading,
    error,
    refreshPrograms,
    eligibilityLoading,
    eligibilityError,
    eligibilityData,
    checkEligibilityForSchool,
    clearEligibility,
    calculateAllEligibility,
    calculateEligibilityForSchools,
    calculateEligibilityForAllPrograms,
    isEligibilityCalculated,
    isEligibilityCalculatedForSchools,
    getProgramEligibility,
    refreshEligibilityIfNeeded,
    eligibilityCalculationInProgress,
    exportEligibilityCacheToFirebase,
    // Firebase-only architecture state
    cacheLoading,
    cacheError,
    userDataStatus,
    showOnboardingModal,
    setShowOnboardingModal,
    triggerEligibilityCalculation,
    globalEligibilityCache,
    handleOnboardingComplete,
    clearErrors,
    clearEligibilityError,
    forceClearAllErrors,
    forceClearCacheAndErrors,
  };

  return (
    <ProgramsContext.Provider value={value}>
      {children}
    </ProgramsContext.Provider>
  );
}; 